import { Reveal } from "@/components/bits";
import { FeaturedQuote, Hl } from "@/components/Testimonios";
import { BookOpen, MessagesSquare, Eye } from "lucide-react";

const GUIDE_IMG = "https://customer-assets.emergentagent.com/job_critica-argentina/artifacts/yv7nga0x_Aldun_79.jpg";

const TableIcon = () => (
  <svg viewBox="0 0 96 96" fill="none" className="h-6 w-6" aria-hidden="true">
    <circle cx="48" cy="48" r="20" stroke="currentColor" strokeWidth="2.5" />
    {[
      [80, 48], [70.6, 70.6], [48, 80], [25.4, 70.6],
      [16, 48], [25.4, 25.4], [48, 16], [70.6, 25.4],
    ].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="3.5" fill="currentColor" />
    ))}
  </svg>
);

const FEATURES = [
  {
    icon: TableIcon,
    text: "Un círculo íntimo de 8 lectoras curiosas y dedicadas.",
  },
  {
    icon: BookOpen,
    text: "No cualquier PDF: guías con mucha data interesante y al pie, para no aburrirte y seguir nutriendo tu mirada.",
  },
  {
    icon: MessagesSquare,
    text: "Tertulias en vivo los lunes, algunas para ubicar a las autoras, otras para diseccionar un poema o fragmento, otras para conectar los diferentes estilos literarios.",
  },
  {
    icon: Eye,
    text: "Lectura crítica real: salís con herramientas para leer profundo cualquier libro que caiga en tus manos. Afilá tu ojo crítico y descubrí todo lo que antes no veías.",
  },
];

const LUNES = [
  <>
    Vamos a explorar juntas{" "}
    <strong className="font-semibold text-gold">el lugar de cada escritora en la literatura argentina</strong>, porque
    el contexto siempre importa. Cuándo escribieron; dónde; en qué tradición o movimiento se insertan y con qué textos
    o autores buscan entrar en diálogo.
  </>,
  <>
    En cada tertulia, haremos un{" "}
    <strong className="font-semibold text-gold">análisis quirúrgico de un poema, cuento o novela</strong> de la
    autora: cómo cuenta, qué recursos utiliza y sobre qué estructura, a partir de fragmentos y citas textuales.
  </>,
  <>
    <strong className="font-semibold text-gold">Incorporamos herramientas de teoría literaria</strong>: conceptos del
    gótico, teoría feminista, narratología y otras perspectivas críticas que nos permiten abrir nuevas preguntas, de
    manera dinámica y convocante.
  </>,
  <>
    <strong className="font-semibold text-gold">Hacemos dialogar a las autoras</strong>: contrastamos sus universos
    literarios. Habrá tertulias dedicadas a cada autora o texto, y otras para trabajar en paralelo las similitudes y
    diferencias entre ellas.
  </>,
];

export default function Programa() {
  return (
    <section id="programa" data-testid="programa-section" className="bg-ink py-20 sm:py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mb-24">
          <p className="flex items-center gap-4 text-[11px] font-medium uppercase tracking-[0.3em] text-gold">
            <span className="h-px w-10 bg-gold/60" /> Cada encuentro
          </p>
          <h3
            data-testid="entrenar-mirada-title"
            className="mt-8 max-w-2xl font-serif text-3xl leading-[1.12] tracking-tight text-cream sm:text-4xl lg:text-5xl"
          >
            Entrená <span className="font-semibold not-italic text-gold">la mirada</span>
          </h3>
          <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-ash/90">
            Seremos un círculo de pocas lectoras que aman la literatura y tienen ganas de llevar sus lecturas más
            lejos.
          </p>
          <div className="mt-10 grid grid-cols-1 gap-px bg-cream/10 md:grid-cols-2">
            {LUNES.map((e, i) => (
              <div key={i} data-testid={`entrenar-mirada-${i}`} className="bg-coal p-8">
                <p className="font-serif text-2xl text-gold/70">{String(i + 1).padStart(2, "0")}</p>
                <p className="mt-4 text-base font-light leading-relaxed text-bone/85">{e}</p>
              </div>
            ))}
          </div>
          <div className="mt-16 max-w-3xl border-l-2 border-crimson-light pl-6 sm:pl-8">
            <p className="font-serif text-2xl leading-snug text-cream sm:text-3xl">
              Cuatro autoras como puerta de entrada a{" "}
              <span className="font-semibold not-italic text-gold">una forma más profunda y afilada de leer.</span>
            </p>
            <p className="mt-5 text-base font-light leading-relaxed text-ash/90">
              Leerlas es también aprender a mirar de otro modo la literatura, el mundo y a nosotras mismas.
            </p>
          </div>
          <div className="mt-16">
            <FeaturedQuote testid="testimonio-amalia" name="Amalia" place="Lectora del Club · desde febrero">
              “Comencé a participar en La tertulita en febrero porque quería retomar el hábito de leer, que había
              perdido por el trajín cotidiano. Y no me equivoqué: el club me permitió{" "}
              <Hl>reencontrarme con mi afición a los libros</Hl> y me brindó{" "}
              <Hl>herramientas para hacerlo de un modo distinto</Hl>. Aprendí a fijar mi atención no sólo en las
              historias, sino en <Hl>desde qué voz se cuentan, con qué metáforas o giros</Hl>; y así el relato se
              enriquece. Además, conecté con estilos que no conocía y pude <Hl>discernir qué voces me convocan</Hl>.”
            </FeaturedQuote>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <p className="flex items-center gap-4 text-[11px] font-medium uppercase tracking-[0.3em] text-gold">
              <span className="h-px w-10 bg-gold/60" /> Sobre Bestias
            </p>
            <h2 className="mt-8 font-serif text-3xl leading-[1.12] tracking-tight text-cream sm:text-4xl lg:text-5xl">
              Es un <span className="font-semibold not-italic text-gold">acto de iniciación</span>
              <br />
              literaria.
            </h2>
            <p className="mt-6 text-lg font-light text-gold xl:whitespace-nowrap">
              Una inmersión directa en la literatura más potente que tenemos.
            </p>
            <div className="relative mt-14">
              <div className="absolute -left-3 -top-3 h-full w-full border border-gold/30" aria-hidden="true" />
              <img
                src={GUIDE_IMG}
                alt="Tu guía de lectura"
                data-testid="programa-guia-image"
                className="relative w-full object-cover"
              />
            </div>
            <p className="mt-6 flex items-center gap-4 text-[10px] font-medium uppercase tracking-[0.3em] text-ash">
              <span className="h-px w-8 bg-crimson-light/70" /> Tu guía · acompañamiento cercano
            </p>
          </Reveal>

          <Reveal delay={0.15} className="self-center">
            <div className="space-y-6" data-testid="programa-list">
              {FEATURES.map((f, i) => (
                <div
                  key={i}
                  className="flex items-start gap-6 border border-cream/15 bg-coal p-6 transition-colors duration-500 hover:border-gold/40 sm:p-7"
                  data-testid={`programa-feature-${i}`}
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-gold/30 text-gold">
                    <f.icon className="h-6 w-6" strokeWidth={1.8} />
                  </span>
                  <p className="self-center text-base font-light leading-relaxed text-bone/85">{f.text}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
